import {
  createContext,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery, UseInfiniteQueryResult, useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { format } from "date-fns";
import {
  MetadataI,
} from "../../../../services/masterDepartmentService";
import { getBusinessSegment } from "../../../../services/businessService/businessService";
import useAuth from "../../../../components/Authentication/useAuth";
import { IGetBusinessResponse } from "../../../../services/businessService/types";
import {
  getCashOnHand,
  getDetailTrxWithdrawal,
  getTrxWithdrawal,
  getWithdrawalDoc,
  IGetCashOnHandResponse,
  IGetDetailTrxWithdrawalResponse,
  IGetWithdrawalData,
  IGetWithdrawalHistoryData,
  postTrxWithdrawal,
} from "../../../../services/trxWithdrawalService";
import { getTrxType } from "../../../../services/trxTypeService/trxTypeService";
import { IGetTrxTypeResponse } from "../../../../services/trxTypeService/types";
import { getMasterCompanyRelationByCompanyId, IGetMasterCompanyResponse } from "../../../../services/masterCompanyService";
import { useDebounce } from "../../../../hooks";
import useWithdrawalHistory, { FilterI as HistoryFilterI } from "./useWithdrawalHistory";
import { getDocUrl } from "../../../../services/bbOneGcsService";

export interface FilterI {
  company?: {
    label: string,
    value: string,
  },
  companyRelation?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  areaId?: string,
  dates?: Date[],
  search?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

export type WithdrawalModalType = "DETAIL" | "FILTER" | "CONFIRM_WITHDRAW"
interface ContextValueI {
  params: FilterI;
  setParams: Dispatch<SetStateAction<FilterI>>;
  meta: MetadataI;
  setMeta: Dispatch<SetStateAction<MetadataI>>;

  withdrawalList?: IGetWithdrawalData[];
  isFetching: boolean;
  isFetchingNextPage: boolean;
  obsRef: MutableRefObject<any>;
  tableRef: MutableRefObject<any>;
  dataTrxType?: IGetTrxTypeResponse;
  dataCompanies?: IGetBusinessResponse;
  dataCompanyRelations?: IGetMasterCompanyResponse;
  modalConfig?: { id?: string, type: WithdrawalModalType };
  setModalConfig: Dispatch<SetStateAction<{ id?: string, type: WithdrawalModalType } | undefined>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  searchHistory: string;
  setSearchHistory: Dispatch<SetStateAction<string>>;
  selectedRows: IGetWithdrawalData[];
  setSelectedRows: Dispatch<SetStateAction<IGetWithdrawalData[]>>;
  totalReceiptAmount: number;
  withdrawAmount: number;
  setWithdrawAmount: Dispatch<SetStateAction<number>>;
  withdrawDate?: Date;
  setWithdrawDate: Dispatch<SetStateAction<Date | undefined>>;
  withdrawDesc: string;
  setWithdrawDesc: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  dataCompanyCash?: IGetCashOnHandResponse;
  expandedHistoryId: string;
  setExpandedHistoryId: Dispatch<SetStateAction<string>>;

  historyObsRef: MutableRefObject<any>;
  historyTableRef: MutableRefObject<any>;
  historyMeta: MetadataI;
  historyParams: HistoryFilterI;
  setHistoryParams: Dispatch<SetStateAction<HistoryFilterI>>;
  historyParamsCount: number;
  tempHistoryParams: HistoryFilterI;
  setTempHistoryParams: Dispatch<SetStateAction<HistoryFilterI>>;
  withdrawalHistoryList: IGetWithdrawalHistoryData[];
  historyQuery: UseInfiniteQueryResult<IGetWithdrawalHistoryData[] | never[], unknown>;
  dataReceiptDetail?: IGetDetailTrxWithdrawalResponse;

  handleCheckRow: (item: any) => void;
  handleCheckAllRows: () => void;
  handleWithdraw: () => void;
  handleDownloadXls: () => void;
}

export const Context = createContext<ContextValueI | undefined>(undefined);
export const useWithdrawalReceipt = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("useWithdrawalReceipt must be used within a Provider component");
  }

  return context;
};

export const Provider = ({ children }: PropsWithChildren) => {
  const { userAccount, userInfo } = useAuth();
  const snackbar = useSnackbar();
  const {
    historyObsRef,
    historyTableRef,
    historyMeta,
    searchHistory,
    setSearchHistory,
    expandedHistoryId,
    setExpandedHistoryId,
    historyParams,
    setHistoryParams,
    historyParamsCount,
    tempHistoryParams,
    setTempHistoryParams,
    withdrawalHistoryList,
    historyQuery,
  } = useWithdrawalHistory();

  const [params, setParams] = useState<FilterI>({
    sortBy: "trx_date",
    sortType: "DSC",
  });
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [modalConfig, setModalConfig] = useState<{ id?: string, type: WithdrawalModalType }>();
  const [searchText, setSearchText] = useState("");
  const [withdrawalList, setWithdrawalList] = useState<IGetWithdrawalData[]>([]);
  const [selectedRows, setSelectedRows] = useState<IGetWithdrawalData[]>([]);
  const [totalReceiptAmount, setTotalReceiptAmount] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [withdrawDate, setWithdrawDate] = useState<Date | undefined>();
  const [withdrawDesc, setWithdrawDesc] = useState("");
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  const fetchList = async (pageParam: number) => {
    const sortBy = params.sortType === "DEFAULT" ? "" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    const hasRequiredParams = !!(params.pool && params.areaId && params.company?.value);

    return hasRequiredParams
      ? getTrxWithdrawal({
        area_id: params.areaId,
        company_code: params.company?.value,
        company_relations: params.companyRelation?.value,
        search: params.search,
        sort_by: sortBy,
        sort_type: sortType,
        page_number: pageParam,
        page_size: 10,
        pool_id: params.pool?.value,
      })
        .then((res) => {
          if (res.metadata) setMeta(res.metadata);
          return res.list_trx_withdrawal;
        })
        .catch(() => [])
      : [];
  };

  const {
    data: withdrawalPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,
  } = useInfiniteQuery(
    ["withdrawalList", params],
    async ({ queryKey, pageParam = 1 }) => fetchList(pageParam),
    {
      getNextPageParam: () => (meta.page < meta.total_pages ? meta.page + 1 : undefined),
      keepPreviousData: true,
    },
  );

  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));
  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      } else if (meta.page === 1 && meta.total_pages > 1 && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  useEffect(() => {
    setWithdrawalList(withdrawalPages?.pages.flatMap((item) => item));
  }, [withdrawalPages]);

  const { data: dataTrxType } = useQuery({
    queryKey: "get_trx_type",
    queryFn: () => getTrxType(),
  });

  const { data: dataCompanyCash, refetch: refetchCompanyCash } = useQuery({
    queryKey: ["get_company_cash", params.pool?.value, params.areaId, params.company?.value],
    enabled: !!params.pool?.value && !!params.areaId && !!params.company?.value,
    queryFn: () => (
      !!params.pool?.value && !!params.areaId && !!params.company?.value
        ? getCashOnHand(
          params.pool?.value || "",
          params.areaId as string,
          params.company?.value || "",
        )
        : undefined
    ),
  });

  const { data: dataCompanies } = useQuery({
    queryKey: ["get_companies", params.pool, params.areaId],
    enabled: !!params.pool && !!params.areaId,
    queryFn: () => getBusinessSegment(
      userAccount.position_info[0].code_type,
      params.areaId as string,
      params.pool?.value as string,
    ),
  });

  const { data: dataCompanyRelations } = useQuery({
    queryKey: ["get_company_relations", params.company?.value],
    enabled: !!params.company?.value,
    queryFn: () => getMasterCompanyRelationByCompanyId(params.company?.value as string),
  });

  const { data: dataReceiptDetail } = useQuery({
    queryKey: ["get_detail_trx_withdrawal", modalConfig?.id],
    enabled: !!modalConfig,
    queryFn: ({ queryKey }) => (queryKey[1]
      ? getDetailTrxWithdrawal(queryKey[1])
      : undefined
    ),
  });

  const resetWithdrawalForm = () => {
    setWithdrawAmount(0);
    setWithdrawDate(undefined);
    setWithdrawDesc("");
  };

  useEffect(() => {
    const currTotalReceiptAmount = selectedRows.reduce(
      (prev, curr) => prev + Number(curr.amount),
      0,
    );

    setTotalReceiptAmount(currTotalReceiptAmount);
    if (selectedRows.length === 0) resetWithdrawalForm();
  }, [selectedRows]);

  useEffect(() => {
    resetWithdrawalForm();
    setSelectedRows([]);
  }, [params]);

  const handleCheckRow = (row: any) => {
    setSelectedRows((curr) => {
      const selectedRowId = curr.findIndex((selected) => selected.trx_id === row.trx_id);
      if (selectedRowId === -1) return [...curr, row];
      const newSelectedRows = [...curr];
      newSelectedRows.splice(selectedRowId, 1);
      return newSelectedRows;
    });
  };
  const handleCheckAllRows = () => {
    setSelectedRows((curr) => {
      if (curr.length === withdrawalList.length) return [];
      return withdrawalList;
    });
  };

  const handleWithdraw = async () => {
    setLoading(true);

    const payload = ({
      trx_id: selectedRows.reduce((prev, curr, i) => (`${prev}${i === 0 ? "" : ","}${curr.trx_id}`), ""),
      area_id: params.areaId || "",
      pool_id: params.pool?.value || "",
      company_code: params.company?.value || "",
      company_relations: params.companyRelation?.value || "",
      value_date: withdrawDate ? format(withdrawDate, "yyyyMMdd") : "",
      trx_desc: withdrawDesc,
      withdrawal_amount: withdrawAmount,
      total_payment: totalReceiptAmount,
      requestor: userInfo.preferred_username,
      ref_correction: "",
      create_by: userInfo.preferred_username,
    });

    await postTrxWithdrawal(payload)
      .then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          refetchCompanyCash();
          resetWithdrawalForm();
          setSelectedRows([]);
          setModalConfig(undefined);
          snackbar.show("Pencairan berhasil", "success");
        } else snackbar.show(res.responseMessage || "Pencairan gagal", "error");
      })
      .catch(() => snackbar.show("Pencairan gagal", "error"))
      .finally(() => setLoading(false));
  };

  const downloadImage = (url: string, fileName?: string) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        if (fileName) a.download = fileName;
        a.href = blobUrl;
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  };

  const handleDownloadXls = async () => {
    setLoading(true);
    await getWithdrawalDoc({
      code_type: historyParams.codeType?.value || "",
      area_id: historyParams.areaId,
      pool_id: historyParams.pool?.value || "",
      company_code: historyParams.company?.value || "",
      company_relations: historyParams.companyRelation?.value,
      period_month_year: historyParams.date || "",
    }).then(async (res) => {
      if (res.response_code === "Succeed" && res.file_dentity) {
        const docUrlString = await getDocUrl({
          file_name: res.file_dentity.file_name,
          path_file: res.file_dentity.file_path,
        });
        downloadImage(docUrlString, res.file_dentity.file_name);
      } else snackbar.show(res.response_message || "Gagal download xls", "error");
    });
    setLoading(false);
  };

  return (
    <Context.Provider
      value={{
        params,
        setParams,
        meta,
        setMeta,

        withdrawalHistoryList,

        withdrawalList,
        isFetching,
        isFetchingNextPage,
        obsRef,
        tableRef,
        dataTrxType,
        dataCompanies,
        dataCompanyRelations,
        modalConfig,
        setModalConfig,
        searchText,
        setSearchText,
        searchHistory,
        setSearchHistory,
        selectedRows,
        setSelectedRows,
        totalReceiptAmount,
        withdrawAmount,
        setWithdrawAmount,
        withdrawDate,
        setWithdrawDate,
        withdrawDesc,
        setWithdrawDesc,
        isLoading,
        dataCompanyCash,
        expandedHistoryId,
        setExpandedHistoryId,
        dataReceiptDetail,

        historyObsRef,
        historyTableRef,
        historyMeta,
        historyParams,
        setHistoryParams,
        historyParamsCount,
        tempHistoryParams,
        historyQuery,
        setTempHistoryParams,

        handleCheckRow,
        handleCheckAllRows,
        handleWithdraw,
        handleDownloadXls,
      }}>

      {children}
    </Context.Provider>
  );
};
